// Transit investment view: pure helpers (selection, statistics, deck.gl layers).
// Data comes from static files written by scripts/rank_routes.py:
//   /data/transit/investment.json       ranked lines (best first) and the settings used
//   /data/transit/investment_hex8.json  per res-8 hexagon: households, low-income households,
//                                       weekday trips within walking distance, and which ranked lines serve it
//   /data/transit/eqbudget{10,25,50}/   the same extra cost as +10%, +25% or +50% more trips
import { PolygonLayer, GeoJsonLayer } from '@deck.gl/layers';
import { cellToBoundary } from 'h3-js';
import { incomeColor } from './mapUtils';

// A populated hexagon is "underserved" with fewer weekday trips than this within a walk of its centre.
export const GAP_TRIPS = 100;
// ...and a gap candidate when at least this share of its households are low-income.
export const GAP_LOW_SHARE = 0.45;
export const GAP_MIN_HH = 100;

const TEAL = [29, 200, 160];
const AMBER = [255, 196, 40];

export function buildCells(hexDoc) {
  return Object.entries(hexDoc.hex).map(([h3id, v]) => ({
    h3id, taz: v[0], hh: v[1], low: v[2], before: v[3], idx: v[4],
    share: v[1] > 0 ? v[2] / v[1] : null,
    polygon: cellToBoundary(h3id).map(([lat, lng]) => [lng, lat]),
  }));
}

/** Line indexes ordered by ranking metric. 'count': low-income households within a walk;
 *  'reach': low-income households in thinly served hexagons that the line reaches. */
export function orderLines(lines, metric) {
  const f = metric === 'reach' ? 'low_income_underserved_reach' : 'low_income_households_served';
  return lines.map((_, i) => i).sort((x, y) => (lines[y][f] || 0) - (lines[x][f] || 0));
}

/** Take lines from the top of `order` until they make up `targetPct` percent of weekday trips. */
export function selectLines(lines, order, targetPct) {
  const sel = new Set();
  let cum = 0;
  for (let k = 0; k < order.length && cum < targetPct; k += 1) {
    sel.add(order[k]);
    cum += lines[order[k]].service_share_pct;
  }
  return { sel, cum };
}

/** Extra weekday trips within walking distance of a hexagon, from the boost on selected lines. */
export function addedTrips(cell, lines, sel, boost) {
  let add = 0;
  for (let k = 0; k < cell.idx.length; k += 1) {
    const i = cell.idx[k];
    if (sel.has(i)) add += lines[i].trips_per_weekday * boost;
  }
  return add;
}

const isGapCandidate = (c) => c.share != null && c.share >= GAP_LOW_SHARE && c.hh >= GAP_MIN_HH;

/** totalTrips: weekday trips of every line, the base the selection's service share uses
 *  (lines holds only the ranked ones). */
export function summarize(cells, lines, sel, boost, totalTrips) {
  let lowBetter = 0, hhBetter = 0, gapBefore = 0, gapAfter = 0, lowGapBefore = 0, lowGapAfter = 0;
  let lowTot = 0, lowTripsB = 0, lowTripsA = 0;
  for (const c of cells) {
    const add = addedTrips(c, lines, sel, boost);
    lowTot += c.low; lowTripsB += c.low * c.before; lowTripsA += c.low * (c.before + add);
    if (add > 0) { lowBetter += c.low; hhBetter += c.hh; }
    if (isGapCandidate(c)) {
      if (c.before < GAP_TRIPS) { gapBefore += 1; lowGapBefore += c.low; }
      if (c.before + add < GAP_TRIPS) { gapAfter += 1; lowGapAfter += c.low; }
    }
  }
  let extra = 0, ranked = 0;
  lines.forEach((l, i) => { ranked += l.trips_per_weekday; if (sel.has(i)) extra += l.trips_per_weekday * boost; });
  const total = totalTrips || ranked;
  return {
    linesFunded: sel.size, extraTrips: Math.round(extra), extraPct: total ? (100 * extra) / total : 0,
    lowBetter: Math.round(lowBetter), hhBetter: Math.round(hhBetter),
    gapBefore, gapAfter, lowGapBefore: Math.round(lowGapBefore), lowGapAfter: Math.round(lowGapAfter),
    lowAvgBefore: lowTot ? lowTripsB / lowTot : 0, lowAvgAfter: lowTot ? lowTripsA / lowTot : 0,
  };
}

// One extra weekday cost spent three ways: each bundle's investment.json flags the lines it funds.
export const SAME_BUDGET = ['eqbudget10', 'eqbudget25', 'eqbudget50'];
export const sameBudgetUrl = (key) => `/data/transit/${key}/investment.json`;
export const SAME_BUDGET_COSTS_URL = '/data/transit/eqbudget50/transit_costs.json';

/** Each bundle's funded lines as indexes into `lines`, matched by agency and line, with the
 *  explore tiles' statistics. `missing`: funded lines that are not in `lines`. */
export function sameBudgetPresets(lines, cells, totalTrips, bundles) {
  const at = new Map(lines.map((l, i) => [`${l.agency}|${l.line}`, i]));
  return bundles.map(({ key, doc }) => {
    const sel = new Set();
    const missing = [];
    let funded = 0, cum = 0;
    for (const l of doc.lines) {
      if (!l.selected) continue;
      funded += 1;
      const i = at.get(`${l.agency}|${l.line}`);
      if (i === undefined) missing.push(`${l.line} (${l.agency})`);
      else { sel.add(i); cum += lines[i].service_share_pct; }
    }
    const { boost } = doc.meta;
    return { key, boost, funded, sel, cum, missing, stats: summarize(cells, lines, sel, boost, totalTrips) };
  });
}

/** Extra weekday cost of the lines a transit_costs.json funds. The flag is written as the string 'True'. */
export function fundedCost(costsDoc) {
  let usd = 0;
  for (const l of costsDoc.lines) {
    if (l.selected_in_rank_scenario === true || l.selected_in_rank_scenario === 'True') usd += l.extra_cost_per_weekday_usd;
  }
  return usd;
}

function fillFor(mode, d, lines, sel, boost) {
  if (mode === 'lines') return incomeColor(d.share, d.hh);
  const add = addedTrips(d, lines, sel, boost);
  if (mode === 'access') {
    if (add <= 0) return [120, 120, 120, d.hh >= 30 ? 28 : 8];
    const t = Math.min(1, add / 60);
    return [...TEAL, Math.round(50 + t * 170)];
  }
  // gaps
  if (!isGapCandidate(d)) return [120, 120, 120, 22];
  if (d.before >= GAP_TRIPS) return [140, 90, 200, 80];
  return d.before + add >= GAP_TRIPS ? [...TEAL, 220] : [230, 90, 60, 205];
}

export function showTip(x, y, html) {
  const tooltip = document.getElementById('tooltip');
  if (!tooltip) return;
  if (html) {
    const canvas = document.querySelector('canvas');
    const rect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0 };
    tooltip.style.display = 'block';
    tooltip.style.left = `${rect.left + x + 12}px`;
    tooltip.style.top = `${rect.top + y + 10}px`;
    tooltip.innerHTML = html;
  } else {
    tooltip.style.display = 'none';
  }
}

export function getInvestmentHexLayer(cells, mode, lines, sel, boost, { pickable = true } = {}) {
  if (!cells || cells.length === 0) return null;
  const key = `${mode}|${[...sel].join(',')}|${boost}`;
  return new PolygonLayer({
    id: 'inv-hex-layer',
    data: cells,
    pickable,
    stroked: true,
    filled: true,
    lineWidthUnits: 'pixels',
    getPolygon: (d) => d.polygon,
    getFillColor: (d) => fillFor(mode, d, lines, sel, boost),
    getLineColor: (d) => (mode === 'access' && d.share != null && d.share >= GAP_LOW_SHARE
      && addedTrips(d, lines, sel, boost) > 0 ? [...AMBER, 230] : [255, 255, 255, 20]),
    getLineWidth: (d) => (mode === 'access' && d.share != null && d.share >= GAP_LOW_SHARE
      && addedTrips(d, lines, sel, boost) > 0 ? 2 : 0.5),
    updateTriggers: { getFillColor: [key], getLineColor: [key], getLineWidth: [key] },
    onHover: ({ object, x, y }) => {
      if (!object) return showTip(0, 0, null);
      const add = addedTrips(object, lines, sel, boost);
      const share = object.share == null ? 'n/a' : `${Math.round(object.share * 100)}%`;
      return showTip(x, y,
        `<b>Zone ${object.taz}</b><br/>${object.hh.toLocaleString()} households, ${share} low-income`
        + `<br/>Weekday trips within a walk: ${object.before.toLocaleString()}`
        + (add > 0 ? ` → ${Math.round(object.before + add).toLocaleString()}` : ''));
    },
  });
}

export function getInvestmentRouteLayer(routesGeo, lines, sel, boost) {
  if (!routesGeo || !routesGeo.features) return null;
  const lineOf = new Map();
  sel.forEach((i) => lines[i].route_ids.forEach((r) => lineOf.set(r, lines[i])));
  const features = routesGeo.features.filter((f) => lineOf.has(f.properties.route_id));
  if (features.length === 0) return null;
  return new GeoJsonLayer({
    id: 'inv-routes-layer',
    data: { type: 'FeatureCollection', features },
    pickable: true,
    lineWidthUnits: 'pixels',
    lineWidthMinPixels: 4,
    getLineWidth: 4,
    getLineColor: [...AMBER, 255],
    updateTriggers: { getLineColor: [features.length] },
    onHover: ({ object, x, y }) => {
      if (!object) return showTip(0, 0, null);
      const l = lineOf.get(object.properties.route_id);
      return showTip(x, y,
        `<b>${l.line}</b> · ${l.agency}<br/>${l.trips_per_weekday} → ${Math.round(l.trips_per_weekday * (1 + boost))} trips / weekday`
        + `<br/>${l.low_income_households_served.toLocaleString()} low-income households within a walk`);
    },
  });
}
